import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { getReferralsByPatient } from "../APIs/referralApi";
import PatientReferralIntakeModal from "./PatientReferralIntakeModal";
import ReferralSlotSelectionModal from "./ReferralSlotSelectionModal";
import ReferralSpecialistSelectionModal from "./ReferralSpecialistSelectionModal";

const TABS = [
  {
    key: "awaiting",
    label: "Awaiting",
    statuses: new Set([
      "PENDING_ACCEPTANCE",
      "PENDING_PATIENT_SELECTION",
      "MATCHING",
      "EXTERNAL_PENDING",
      "EXTERNAL_SENT",
      "EXTERNAL_FAX_FAILED",
    ]),
  },
  {
    key: "manual_review",
    label: "Manual Review",
    statuses: new Set(["MANUAL_REVIEW", "INFO_REQUESTED"]),
  },
  {
    key: "accepted",
    label: "Accepted",
    statuses: new Set([
      "ACCEPTED",
      "APPOINTMENT_OFFERED",
      "SCHEDULED",
      "EXTERNAL_ACCEPTED",
    ]),
  },
  {
    key: "completed",
    label: "Completed",
    statuses: new Set(["COMPLETED", "CLOSED"]),
  },
  {
    key: "declined",
    label: "Declined",
    statuses: new Set(["DECLINED", "EXTERNAL_DECLINED"]),
  },
];

const STATUS_LABEL = {
  PENDING_ACCEPTANCE: "Awaiting specialist response",
  PENDING_PATIENT_SELECTION: "Choose your specialist",
  MATCHING: "Finding you a specialist",
  MANUAL_REVIEW: "We're working on scheduling — you'll be notified shortly",
  INFO_REQUESTED: "Your provider requested more information",
  ACCEPTED: "Specialist accepted",
  APPOINTMENT_OFFERED: "Appointment slots available — pick a time",
  SCHEDULED: "Appointment booked",
  COMPLETED: "Visit completed",
  CLOSED: "Referral closed",
  DECLINED: "Specialist declined this referral",
  EXTERNAL_PENDING: "Referral sent — awaiting specialist response",
  EXTERNAL_SENT: "Referral delivered — awaiting specialist response",
  EXTERNAL_ACCEPTED: "Specialist accepted your referral",
  EXTERNAL_DECLINED: "Specialist declined this referral",
  EXTERNAL_FAX_FAILED: "Delivery issue — your provider is following up",
};

const getStatusLabel = (r) => {
  if (
    r.status === "PENDING_ACCEPTANCE" &&
    r.routingMode === "SMART" &&
    r.specialistName
  ) {
    return `Assigned to Dr. ${r.specialistName} — Pending Acceptance`;
  }
  if (
    r.status === "PENDING_PATIENT_SELECTION" &&
    r.matchAttempts?.some((a) => a.status === "DECLINED")
  ) {
    return "Your selected specialist is unavailable — please choose another";
  }
  return STATUS_LABEL[r.status] ?? r.status;
};

const URGENCY_LABEL = {
  ROUTINE: "Routine",
  WITHIN_30_DAYS: "Within 30 Days",
  WITHIN_7_DAYS: "Within 7 Days",
  WITHIN_3_DAYS: "Within 3 Days",
};

export default function PatientReferralsTab({ patientId }) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("awaiting");
  const [expanded, setExpanded] = useState({});
  const [slotsModal, setSlotsModal] = useState(null);
  const [specialistModal, setSpecialistModal] = useState(null);
  const [sourceTab, setSourceTab] = useState("received");
  const [showReferralIntake, setShowReferralIntake] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReferralsByPatient(patientId);
      setReferrals(Array.isArray(data) ? data : (data.content ?? []));
    } catch (err) {
      console.error("Failed to load patient referrals:", err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const sourceReferrals = referrals.filter((r) =>
    sourceTab === "sent"
      ? r.createdByType === "PATIENT" &&
        Number(r.createdByPatientId) === Number(patientId)
      : r.createdByType !== "PATIENT",
  );

  const tabReferrals = (key) => {
    const tab = TABS.find((t) => t.key === key);
    return tab ? sourceReferrals.filter((r) => tab.statuses.has(r.status)) : [];
  };

  if (loading)
    return (
      <p className="text-sm text-gray-500 py-6 text-center">
        Loading referrals...
      </p>
    );

  const visibleList = tabReferrals(activeTab);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex rounded-lg border bg-white p-1">
          {[
            { key: "received", label: "Received" },
            { key: "sent", label: "Sent" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setSourceTab(tab.key);
                setActiveTab("awaiting");
              }}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
                sourceTab === tab.key
                  ? "bg-teal-600 text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowReferralIntake(true)}
          className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700"
        >
          + New Referral
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b mb-4">
        {TABS.map((t) => {
          const count = tabReferrals(t.key).length;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
              {count > 0 && (
                <span
                  className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === t.key
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Cards */}
      {visibleList.length === 0 ? (
        <p className="py-12 text-center text-gray-400 text-sm">
          No referrals here yet.
        </p>
      ) : (
        <div className="space-y-3">
          {visibleList.map((r) => {
            const isExpanded = expanded[r.referralId];
            const statusLabel = getStatusLabel(r);
            const urgencyLabel = URGENCY_LABEL[r.urgency] ?? r.urgency;

            return (
              <div
                key={r.referralId}
                className="rounded-xl border bg-white shadow-sm overflow-hidden"
              >
                {/* Clickable header */}
                <button
                  className="w-full text-left p-4 hover:bg-gray-50 transition"
                  onClick={() => toggleExpand(r.referralId)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-base font-semibold text-gray-900">
                        {r.specialty ?? "Specialist Referral"}
                      </div>
                      {r.referringProviderName && (
                        <div className="text-sm text-gray-500">
                          {r.createdByType === "PATIENT"
                            ? "Referring provider"
                            : "From"}
                          : {r.referringProviderName}
                        </div>
                      )}
                      {sourceTab === "sent" &&
                        (r.specialistName || r.externalProviderName) && (
                          <div className="text-sm text-gray-500">
                            To: {r.specialistName || r.externalProviderName}
                          </div>
                        )}
                      {r.createdAt && (
                        <div className="text-sm text-gray-400">
                          {new Date(r.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>
                      )}
                      <div className="text-sm text-gray-500 mt-1">
                        {statusLabel}
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-2">
                      {r.urgency && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                          {urgencyLabel}
                        </span>
                      )}
                      <span className="text-gray-400">
                        {isExpanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </span>
                    </div>
                  </div>
                </button>

                {/* Expanded detail section */}
                {isExpanded && (
                  <div className="border-t bg-gray-50 px-4 py-4 space-y-3">
                    <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
                      {r.referringProviderName && (
                        <>
                          <span className="text-gray-500 font-medium whitespace-nowrap">
                            Referring provider
                          </span>
                          <span className="text-gray-800">
                            {r.referringProviderName}
                          </span>
                        </>
                      )}
                      {r.createdAt && (
                        <>
                          <span className="text-gray-500 font-medium whitespace-nowrap">
                            Referred on
                          </span>
                          <span className="text-gray-800">
                            {new Date(r.createdAt).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        </>
                      )}
                      {r.specialty && (
                        <>
                          <span className="text-gray-500 font-medium whitespace-nowrap">
                            Referred for
                          </span>
                          <span className="text-gray-800">{r.specialty}</span>
                        </>
                      )}
                      {r.reason && (
                        <>
                          <span className="text-gray-500 font-medium whitespace-nowrap">
                            Reason
                          </span>
                          <span className="text-gray-800">{r.reason}</span>
                        </>
                      )}
                      {r.specialistName && (
                        <>
                          <span className="text-gray-500 font-medium whitespace-nowrap">
                            Specialist
                          </span>
                          <span className="text-gray-800">
                            Dr. {r.specialistName}
                            {r.status === "PENDING_ACCEPTANCE" &&
                              r.routingMode === "SMART" && (
                                <span className="ml-1.5 text-xs text-yellow-600 font-medium">
                                  (Pending Acceptance)
                                </span>
                              )}
                          </span>
                        </>
                      )}
                      {r.externalProviderName && (
                        <>
                          <span className="text-gray-500 font-medium whitespace-nowrap">
                            Specialist
                          </span>
                          <span className="text-gray-800">
                            {r.externalProviderName}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Action CTAs */}
                    {r.status === "APPOINTMENT_OFFERED" && (
                      <button
                        onClick={() =>
                          setSlotsModal({
                            referralId: r.referralId,
                            providerName: r.specialistName,
                            specialty: r.specialty,
                          })
                        }
                        className="mt-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700"
                      >
                        Pick appointment time →
                      </button>
                    )}
                    {r.status === "PENDING_PATIENT_SELECTION" && (
                      <button
                        onClick={() => setSpecialistModal(r.referralId)}
                        className="mt-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700"
                      >
                        Choose your specialist →
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {slotsModal && (
        <ReferralSlotSelectionModal
          referralId={slotsModal.referralId}
          providerName={slotsModal.providerName}
          specialty={slotsModal.specialty}
          onClose={() => setSlotsModal(null)}
        />
      )}
      {specialistModal && (
        <ReferralSpecialistSelectionModal
          referralId={specialistModal}
          onClose={() => setSpecialistModal(null)}
          onSelected={() => {
            setSpecialistModal(null);
            load();
          }}
        />
      )}
      {showReferralIntake && (
        <PatientReferralIntakeModal
          patientId={patientId}
          onClose={() => setShowReferralIntake(false)}
          onSubmitted={() => {
            setShowReferralIntake(false);
            setSourceTab("sent");
            load();
          }}
        />
      )}
    </>
  );
}
